import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Home, DollarSign, CheckCircle, XCircle, AlertTriangle } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { setCanonical, setPageTitle, setMetaDescription, generateFAQSchema, generateSpeakableSchema, generateBreadcrumbSchema } from "@/utils/seo";
import { calculateBreakdown, formatMoney } from "@/utils/taxCalculator";
import { InlineBanner, BottomAdBanner } from "@/components/SponsorBanners";
import FinancialDisclaimer from "@/components/FinancialDisclaimer";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CanIAffordPage({ rent, salary, citySlug }) {
  const [city, setCity] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!citySlug) return;
    axios.get(`${API}/cities/${citySlug}`).then(r => {
      setCity(r.data);
      setCanonical(`/can-i-afford-${rent}-rent-on-${salary/1000}k-in-${citySlug}`);
      setPageTitle(`Can I Afford $${rent} Rent on ${formatMoney(salary)} in ${r.data.name}?`);
      setMetaDescription(`Find out if you can afford $${rent}/month rent on a ${formatMoney(salary)} salary in ${r.data.name}, ${r.data.state_code}. Full budget breakdown included.`);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [citySlug, rent, salary]);

  if (loading) return <div className="container-main py-24 text-center text-slate-400">Loading...</div>;
  if (!city) return <div className="container-main py-24 text-center"><h1 className="text-2xl font-bold">City Not Found</h1></div>;

  const result = calculateBreakdown(salary, city);
  const customRentRatio = result.net_monthly > 0 ? Math.round((rent / result.net_monthly) * 1000) / 10 : 999;
  const otherExpenses = result.groceries + result.transportation + result.utilities + result.healthcare;
  const remainingWithCustomRent = Math.round((result.net_monthly - rent - otherExpenses) * 100) / 100;

  let canAfford, verdictIcon, verdictText, verdictBg;
  if (remainingWithCustomRent > result.net_monthly * 0.15) {
    canAfford = "yes"; verdictIcon = CheckCircle; verdictText = "Yes, you can afford it"; verdictBg = "bg-emerald-50 border-emerald-200";
  } else if (remainingWithCustomRent > 0) {
    canAfford = "tight"; verdictIcon = AlertTriangle; verdictText = "It's tight, but possible"; verdictBg = "bg-amber-50 border-amber-200";
  } else {
    canAfford = "no"; verdictIcon = XCircle; verdictText = "No, this exceeds your budget"; verdictBg = "bg-red-50 border-red-200";
  }

  const faqs = [
    { question: `Can I afford $${rent}/month rent on ${formatMoney(salary)} in ${city.name}?`, answer: `${verdictText}. On a ${formatMoney(salary)} salary in ${city.name}, your monthly take-home is approximately ${formatMoney(result.net_monthly)}. After $${rent} rent and other expenses, you'd have ${formatMoney(remainingWithCustomRent)} remaining.` },
    { question: `What percentage of my income is $${rent} rent?`, answer: `$${rent}/month would be ${customRentRatio}% of your monthly take-home pay. Financial advisors recommend keeping rent under 30% of take-home income.` },
    { question: `What is the average rent in ${city.name}?`, answer: `The median 1-bedroom rent in ${city.name} is ${formatMoney(city.rent_1br)}/month. ${rent > city.rent_1br ? `$${rent} is above the median.` : `$${rent} is below the median, which is favorable.`}` },
    { question: `What other expenses should I budget for?`, answer: `Beyond rent, budget for groceries (${formatMoney(result.groceries)}), transportation (${formatMoney(result.transportation)}), utilities (${formatMoney(result.utilities)}), and healthcare (${formatMoney(result.healthcare)}) in ${city.name}.` },
  ];

  const VerdictIcon = verdictIcon;

  return (
    <div data-testid="can-i-afford-page">
      <script type="application/ld+json">{JSON.stringify(generateFAQSchema(faqs))}</script>
      <script type="application/ld+json">{JSON.stringify(generateBreadcrumbSchema([
        { name: "Home", url: "/" }, { name: `Can I Afford $${rent} in ${city.name}` }
      ]))}</script>
      <script type="application/ld+json">{JSON.stringify(generateSpeakableSchema(
        `/can-i-afford-${rent}-rent-on-${salary/1000}k-in-${citySlug}`,
        [`Can I Afford $${rent} Rent on ${formatMoney(salary)} in ${city.name}?`],
        [`${verdictText}. Your rent-to-income ratio would be ${customRentRatio}%. You'd have ${formatMoney(remainingWithCustomRent)}/month remaining.`]
      ))}</script>

      <div className="bg-slate-50 border-b border-slate-100 py-3">
        <div className="container-main flex items-center gap-2 text-xs text-slate-500">
          <Link to="/" className="hover:text-slate-700 no-underline">Home</Link><span>/</span>
          <span className="text-slate-700">Can I Afford ${rent} Rent in {city.name}</span>
        </div>
      </div>

      <section className="hero-pattern py-12 md:py-16">
        <div className="container-main max-w-3xl">
          <h1 className="speakable-headline text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 mb-4">
            Can I Afford ${rent} Rent on {formatMoney(salary)} in {city.name}?
          </h1>

          <Card className={`shadow-lg border-2 ${verdictBg} mb-8`}>
            <CardContent className="p-6 md:p-8 text-center">
              <VerdictIcon className={`w-12 h-12 mx-auto mb-3 ${canAfford === 'yes' ? 'text-emerald-600' : canAfford === 'tight' ? 'text-amber-600' : 'text-red-600'}`} />
              <h2 className="text-2xl font-bold text-slate-900 mb-2" data-testid="afford-verdict">{verdictText}</h2>
              <p className="text-sm text-slate-600">
                Your rent-to-income ratio would be <strong>{customRentRatio}%</strong>.
                You'd have <strong className="font-mono">{formatMoney(remainingWithCustomRent)}</strong>/month remaining after all expenses.
              </p>
            </CardContent>
          </Card>

          <Card className="shadow-md">
            <CardContent className="p-6">
              <h3 className="text-sm font-semibold text-slate-900 mb-4">Monthly Budget Breakdown</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b border-slate-100">
                  <span className="text-sm text-slate-600 flex items-center gap-2"><Home className="w-4 h-4" /> Rent</span>
                  <span className="font-mono font-semibold text-slate-900">{formatMoney(rent)}</span>
                </div>
                {[
                  { label: "Groceries", value: result.groceries },
                  { label: "Transportation", value: result.transportation },
                  { label: "Utilities", value: result.utilities },
                  { label: "Healthcare", value: result.healthcare },
                ].map((e, i) => (
                  <div key={i} className="flex justify-between items-center py-2 border-b border-slate-100">
                    <span className="text-sm text-slate-600">{e.label}</span>
                    <span className="font-mono text-slate-700">{formatMoney(e.value)}</span>
                  </div>
                ))}
                <div className="flex justify-between items-center pt-2 font-semibold">
                  <span className="text-slate-900">Net Monthly Income</span>
                  <span className="font-mono text-emerald-700">{formatMoney(result.net_monthly)}</span>
                </div>
                <div className="flex justify-between items-center pt-1 font-semibold">
                  <span className="text-slate-900">Remaining After Expenses</span>
                  <span className={`font-mono ${remainingWithCustomRent >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>{formatMoney(remainingWithCustomRent)}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      <section className="py-12 bg-slate-50">
        <div className="container-main max-w-3xl">
          <FinancialDisclaimer />
          {/* Apartment Affiliate Banner */}
          <a href="https://www.hellolanding.com/r/web/2vvg1H" target="_blank" rel="noopener" className="no-underline block">
            <div className="p-4 bg-gradient-to-r from-blue-50 via-sky-50 to-blue-50 border border-blue-200 rounded-xl mb-6 flex items-center gap-4 hover:shadow-md transition-all" data-testid="post-result-banner">
              <img src="https://images.pexels.com/photos/19899060/pexels-photo-19899060.jpeg?auto=compress&cs=tinysrgb&w=80&h=80&fit=crop" alt="Furnished apartment" className="w-14 h-14 rounded-lg object-cover flex-shrink-0" />
              <div className="flex-1">
                <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-0.5">Sponsored</p>
                <p className="text-sm font-semibold text-slate-900">Moving to {city.name}? Get $250 Off Your First Furnished Apartment</p>
                <p className="text-xs text-slate-500">Move freely, stay comfortably. Furnished apartments in 200+ cities. *Limited time offer. Terms apply.</p>
              </div>
              <span className="text-xs font-bold text-blue-600 whitespace-nowrap hidden md:inline">Claim $250 Off &rarr;</span>
            </div>
          </a>
          <Accordion type="single" collapsible className="space-y-2">
            {faqs.map((faq, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border border-slate-200 rounded-xl px-4 bg-white">
                <AccordionTrigger className="text-sm font-medium text-slate-800 hover:no-underline py-4">{faq.question}</AccordionTrigger>
                <AccordionContent className="text-sm text-slate-600 pb-4">{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <section className="py-12">
        <div className="container-main text-center">
          <Link to="/">
            <Button className="bg-emerald-700 hover:bg-emerald-800" data-testid="back-to-calculator">
              <DollarSign className="w-4 h-4 mr-2" /> Try Different Numbers
            </Button>
          </Link>
        </div>
      </section>

      <BottomAdBanner />
    </div>
  );
}
